import { useCallback } from "react";
import { enqueueSnackbar } from "notistack";
import { read as xlsRead, WorkSheet, CellObject, utils } from "xlsx";
import { hasDuplicateHeaders, detectEmptyHeaders } from "../utils/utils"; // Updated import

interface FileProcessorProps {
  setFileToUpload: React.Dispatch<React.SetStateAction<File | null>>;
  setSelectedSheetName: React.Dispatch<React.SetStateAction<string | null>>;
  setIsLoading: (loading: boolean) => void;
  initializeTable: (file: File, headers: string[], mutationParam: "azure" | "matcher") => Promise<void>;
  mutationParam: "azure" | "matcher";
}

export const useFileProcessor = ({
  setFileToUpload,
  setSelectedSheetName,
  setIsLoading,
  initializeTable,
  mutationParam,
}: FileProcessorProps) => {
  const processCSV = useCallback(
    async (file: File) => {
      const headerChunk = await file?.slice(0, 4096).text();
      const firstLine = headerChunk?.split("\n")[0];
      if (!firstLine) {
        setFileToUpload(null);
        enqueueSnackbar("File is empty or has no valid headers.", {
          variant: "error",
          autoHideDuration: 10000,
          key: "csv-empty",
        });
        return;
      }
      const headers = firstLine
        ?.replace(/,+$/, "")
        .split(",")
        .map((header, index) => (header === "" ? `Column ${index + 1}` : header.trim().replace(/^"|"$/g, "")));

      const nonEmptyHeaders = headers.filter((header) => header !== "");
      const hasEmptyInMiddle = headers.some(
        (header, index) => header === "" && index < headers.length - 1 && index < nonEmptyHeaders.length
      );

      if (hasEmptyInMiddle) {
        setFileToUpload(null);
        enqueueSnackbar(
          "File contains empty headers in the middle of the header row. Please ensure all headers are filled and try again.",
          { variant: "error", autoHideDuration: 10000, key: "csv-empty-middle" }
        );
        return;
      }

      const { hasEmptyHeaders, emptyCount, cleanedHeaders } = detectEmptyHeaders(headers);
      if (hasEmptyHeaders) {
        setFileToUpload(null);
        enqueueSnackbar(`Error: Removed ${emptyCount} empty headers at the end of the header row.`, {
          variant: "error",
          autoHideDuration: 8000,
          key: "csv-trailing-headers",
        });
        return;
      }

      const { hasDuplicates, duplicates } = hasDuplicateHeaders(cleanedHeaders);
      if (hasDuplicates) {
        setFileToUpload(null);
        enqueueSnackbar(
          `File contains duplicate column names: ${duplicates
            .map((col) => `"${col}"`)
            .join(", ")}. Please remove duplicates and try again.`,
          { variant: "error", autoHideDuration: 10000, key: "csv-duplicates" }
        );
        return;
      }

      setFileToUpload(file);
      setSelectedSheetName(null);

      await initializeTable(file, cleanedHeaders, mutationParam);
      enqueueSnackbar("File ready for upload", { variant: "success", autoHideDuration: 3000, key: "csv-success" });
    },
    [setFileToUpload, setSelectedSheetName, initializeTable, mutationParam]
  );

  const processExcel = useCallback(
    async (file: File, sheetName: string) => {
      setIsLoading(true);
      try {
        const arrayBuffer = await file.arrayBuffer();
        const workbook = xlsRead(arrayBuffer, { type: "array", sheets: [sheetName], dense: true });
        const worksheet: WorkSheet = workbook.Sheets[sheetName];

        const range = utils.decode_range(worksheet["!ref"] || "A1:A1");

        const firstRow = Array.from({ length: range.e.c + 1 }, (_, col) => col).reduce<CellObject[]>(
          (acc, col) => [...acc, worksheet[0]?.[col] || { v: "" }],
          []
        );

        if (!firstRow || firstRow.every((cell) => !cell.v || String(cell.v).trim() === "")) {
          setFileToUpload(null);
          enqueueSnackbar(
            "The Excel file's first row is empty or contains no valid headers. Please ensure the first row has at least one non-empty column header and try again.",
            { variant: "error", autoHideDuration: 10000, key: "excel-empty-first-row" }
          );
          return;
        }

        const headers = firstRow.map((cell: CellObject) => (cell.v == null ? "" : String(cell.v).trim()));

        const nonEmptyHeaders = headers.filter((header) => header !== "");
        const hasEmptyInMiddle = headers.some(
          (header, index) => header === "" && index < headers.length - 1 && index < nonEmptyHeaders.length
        );

        if (hasEmptyInMiddle) {
          setFileToUpload(null);
          enqueueSnackbar(
            "File contains empty headers in the middle of the header row. Please ensure all headers are filled and try again.",
            { variant: "error", autoHideDuration: 10000, key: "excel-empty-middle" }
          );
          return;
        }

        const { hasEmptyHeaders, emptyCount, cleanedHeaders } = detectEmptyHeaders(headers);
        if (hasEmptyHeaders) {
          setFileToUpload(null);
          enqueueSnackbar(`File contains ${emptyCount} empty headers at the end of the header row.`, {
            variant: "error",
            autoHideDuration: 8000,
            key: "excel-trailing-headers",
          });
          return;
        }

        const { hasDuplicates, duplicates } = hasDuplicateHeaders(cleanedHeaders);
        if (hasDuplicates) {
          setFileToUpload(null);
          enqueueSnackbar(
            `File contains duplicate column names: ${duplicates
              .map((col) => `"${col}"`)
              .join(", ")}. Please remove duplicates and try again.`,
            { variant: "error", autoHideDuration: 10000, key: "excel-duplicates" }
          );
          return;
        }

        const csvData = utils.sheet_to_csv(worksheet);
        const csvBlob = new Blob([csvData], { type: "text/csv" });
        const csvFile = new File([csvBlob], file.name.replace(/\.[^/.]+$/, ".csv"), {
          type: "text/csv",
        });
        setFileToUpload(csvFile);

        await initializeTable(csvFile, cleanedHeaders, mutationParam);
        enqueueSnackbar("File ready for upload", { variant: "success", autoHideDuration: 3000, key: "excel-success" });
      } catch (e) {
        enqueueSnackbar("Failed to process file", { variant: "error", autoHideDuration: 5000, key: "excel-error" });
      } finally {
        setIsLoading(false);
      }
    },
    [setFileToUpload, setIsLoading, initializeTable, mutationParam]
  );

  return { processCSV, processExcel };
};
